import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, Button, makeStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import GetAppIcon from '@material-ui/icons/GetApp';
import * as XLSX from 'xlsx';

import excelFileUrl from '../../../../../resources/documents/ORDERS05_NewPO_Business_Mapping.xlsx';

const useStyles = makeStyles(() => ({
  fullScreen: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#fff',
    zIndex: 1300,
    overflowY: 'auto',
    padding: '20px 32px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  backBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e0e0e0',
  },
  backBtn: {
    textTransform: 'none',
    fontSize: '0.82rem',
    color: '#444',
    padding: '3px 10px',
    minWidth: 0,
    fontWeight: 500,
    '&:hover': { backgroundColor: '#f0f0f0' },
  },
  pageTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#1a1a2e',
  },
  infoBar: {
    backgroundColor: '#EEF4FB',
    borderRadius: '6px',
    padding: '12px 24px',
    display: 'flex',
    gap: '60px',
    marginBottom: '20px',
    flexWrap: 'wrap',
    width: 'fit-content',
    margin: '0 auto 20px',
  },
  infoItem: {},
  infoLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: '#111',
    marginBottom: '4px',
  },
  infoValue: {
    fontSize: '0.82rem',
    fontWeight: 400,
    color: '#444',
  },
  idocViewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  idocViewTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#1a1a2e',
  },
  downloadBtn: {
    textTransform: 'none',
    fontSize: '0.78rem',
    fontWeight: 600,
    backgroundColor: '#EC6535',
    color: '#fff',
    padding: '5px 16px',
    borderRadius: '4px',
    '&:hover': { backgroundColor: '#d4551f' },
  },
  sheetWrapper: {
    border: '1px solid #aaa',
    borderRadius: '4px',
    overflow: 'auto',
    fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
  },
  colLetterRow: {
    display: 'flex',
    backgroundColor: '#e8e8e8',
    borderBottom: '1px solid #bbb',
    userSelect: 'none',
  },
  colLetterCell: {
    fontSize: '0.65rem',
    color: '#555',
    textAlign: 'center',
    padding: '3px 0',
    borderRight: '1px solid #ccc',
    flexShrink: 0,
  },
  titleRow: {
    backgroundColor: '#1F3864',
    padding: '8px 12px',
    textAlign: 'center',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#fff',
    borderBottom: '1px solid #162d52',
    letterSpacing: '0.02em',
  },
  subHeaderRow: {
    backgroundColor: '#2E75B6',
    padding: '5px 12px',
    fontSize: '0.68rem',
    color: '#fff',
    borderBottom: '1px solid #1f5a96',
  },
  colHeaderRow: {
    display: 'flex',
    backgroundColor: '#BDD7EE',
    borderBottom: '2px solid #9DC3E6',
  },
  rowNumHeaderCell: {
    width: 28,
    minWidth: 28,
    flexShrink: 0,
    borderRight: '1px solid #9DC3E6',
    backgroundColor: '#D9E1F2',
  },
  colHeaderCell: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#1F3864',
    padding: '6px 8px',
    borderRight: '1px solid #9DC3E6',
    flexShrink: 0,
    lineHeight: 1.3,
  },
  dataRow: {
    display: 'flex',
    borderBottom: '1px solid #e8e8e8',
    '&:hover': { backgroundColor: '#f0f7ff' },
  },
  rowNumCell: {
    width: 28,
    minWidth: 28,
    flexShrink: 0,
    backgroundColor: '#f2f2f2',
    borderRight: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.65rem',
    color: '#888',
    userSelect: 'none',
  },
  dataCell: {
    fontSize: '0.72rem',
    color: '#222',
    padding: '6px 8px',
    borderRight: '1px solid #e8e8e8',
    flexShrink: 0,
    lineHeight: 1.45,
    verticalAlign: 'top',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  sectionRow: {
    display: 'flex',
    backgroundColor: '#f5f5dc',
    borderBottom: '1px solid #e0e0e0',
  },
  sectionCell: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#333',
    padding: '6px 8px',
  },
  legendRow: {
    display: 'flex',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e8e8e8',
  },
  legendCell: {
    fontSize: '0.72rem',
    color: '#444',
    padding: '5px 8px',
  },
  tabBar: {
    display: 'flex',
    backgroundColor: '#f0f0f0',
    borderTop: '2px solid #1F3864',
  },
  sheetTab: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#1F3864',
    backgroundColor: '#fff',
    padding: '5px 16px',
    borderRight: '1px solid #ccc',
    borderTop: '2px solid #1F3864',
    marginTop: '-2px',
    cursor: 'pointer',
    userSelect: 'none',
  },
}));

// Column widths
const COL_WIDTHS = [120, 130, 120, 160, 160, 280, 110, 120];
const ROW_NUM_W = 28;
const COL_LETTERS = ['', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
const COL_LETTER_WIDTHS = [ROW_NUM_W, ...COL_WIDTHS];

const IDOCReviewView = ({ onBack }) => {
  const classes = useStyles();
  const [sheetData, setSheetData] = useState(null);
  const [sheetName, setSheetName] = useState('');

  useEffect(() => {
    const loadExcel = async () => {
      try {
        const response = await fetch(excelFileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: 'array' });
        setSheetName(wb.SheetNames[0] || 'Sheet1');
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        setSheetData(data);
      } catch (err) {
        console.error('Failed to load Excel file:', err);
      }
    };
    loadExcel();
  }, []);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = excelFileUrl;
    link.download = 'ORDERS05_NewPO_Business_Mapping.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Parse sheet data into render sections
  const titleText = sheetData && sheetData[0] ? String(sheetData[0][0] || '') : '';
  const subHeaderText = sheetData && sheetData[1] ? String(sheetData[1][0] || '') : '';
  const columnHeaders = sheetData && sheetData[2] ? sheetData[2] : [];
  // Data rows start at index 3, stop at first fully empty row or end
  const dataRows = [];
  if (sheetData) {
    for (let i = 3; i < sheetData.length; i++) {
      const row = sheetData[i];
      const nonEmpty = row.filter(c => c !== '').length;
      if (nonEmpty === 0) break; // stop at first empty row
      dataRows.push({ idx: i, cells: row });
    }
  }

  return ReactDOM.createPortal(
    <Box className={classes.fullScreen}>
      {/* Back bar */}
      <Box className={classes.backBar}>
        <Button
          className={classes.backBtn}
          startIcon={<ArrowBackIcon style={{ fontSize: '1rem' }} />}
          onClick={onBack}
        >
          Back
        </Button>
        <Typography className={classes.pageTitle}>
          IDoc/XML (ORDERS05 New Purchase Order)
        </Typography>
      </Box>

      {/* Info bar */}
      <Box className={classes.infoBar}>
        {[
          { label: 'CMO-MFG',                           value: 'Oxford Biomedica - 1007' },
          { label: 'Product ID / Product Description',   value: '403213 / Paracetamol 50 ml vial' },
          { label: 'CMO Work Order Number',              value: 'CMO12345' },
          { label: 'Batch Number',                       value: '400682' },
        ].map(item => (
          <Box key={item.label} className={classes.infoItem}>
            <Typography className={classes.infoLabel}>{item.label}</Typography>
            <Typography className={classes.infoValue}>{item.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* IDOC View header */}
      <Box className={classes.idocViewHeader}>
        <Typography className={classes.idocViewTitle}>IDoc/XML View</Typography>
        <Button
          variant="contained"
          size="small"
          className={classes.downloadBtn}
          startIcon={<GetAppIcon style={{ fontSize: '0.9rem' }} />}
          onClick={handleDownload}
        >
          Download
        </Button>
      </Box>

      {/* Spreadsheet */}
      <Box className={classes.sheetWrapper}>
        {/* Column letters */}
        <Box className={classes.colLetterRow}>
          {COL_LETTERS.map((ltr, i) => (
            <Box
              key={i}
              className={classes.colLetterCell}
              style={{ width: COL_LETTER_WIDTHS[i], minWidth: COL_LETTER_WIDTHS[i] }}
            >
              {ltr}
            </Box>
          ))}
        </Box>

        {/* Row 1 — Title */}
        <Box className={classes.titleRow}>
          {titleText || 'ORDERS05 IDoc/XML – New Purchase Order | Business Segment Mapping'}
        </Box>

        {/* Row 2 — Sub-header */}
        <Box className={classes.subHeaderRow}>
          {subHeaderText || ''}
        </Box>

        {/* Row 3 — Column headers */}
        <Box className={classes.colHeaderRow}>
          <Box className={classes.rowNumHeaderCell} />
          {columnHeaders.map((col, ci) => (
            <Box
              key={ci}
              className={classes.colHeaderCell}
              style={{ width: COL_WIDTHS[ci] || 120, minWidth: COL_WIDTHS[ci] || 120 }}
            >
              {String(col)}
            </Box>
          ))}
        </Box>

        {/* Data rows */}
        {sheetData ? (
          dataRows.map((row) => (
            <Box key={row.idx} className={classes.dataRow}>
              <Box className={classes.rowNumCell}>{row.idx + 1}</Box>
              {row.cells.map((cell, ci) => {
                const cellStr = String(cell).replace(/\r\n/g, '\n').trim();
                const isMandatory = cellStr === 'Mandatory';
                const isOptional = cellStr === 'Optional';
                const isSegment = ci === 0;
                const isField = ci === 2;
                const isBizTerm = ci === 4;
                const isCond = ci === 1;
                return (
                  <Box
                    key={ci}
                    className={classes.dataCell}
                    style={{
                      width: COL_WIDTHS[ci] || 120,
                      minWidth: COL_WIDTHS[ci] || 120,
                      fontWeight: (isSegment || isField || isBizTerm) ? 600 : 400,
                      color: isMandatory ? '#d32f2f'
                        : isOptional ? '#1976d2'
                        : (isField || isBizTerm) ? '#1F3864'
                        : isCond ? '#888'
                        : ci === 5 ? '#444'
                        : '#222',
                    }}
                  >
                    {cellStr}
                  </Box>
                );
              })}
            </Box>
          ))
        ) : (
          <Box style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            Loading spreadsheet data...
          </Box>
        )}

        {/* Sheet tab bar */}
        <Box className={classes.tabBar}>
          <Box className={classes.sheetTab}>{sheetName || 'IDoc Segment Mapping'}</Box>
        </Box>
      </Box>
    </Box>,
    document.body
  );
};

export default IDOCReviewView;
